/**
 * Subscription utility functions for expiration status, formatting, and display logic.
 */

export type ExpirationStatus = 'none' | 'renew_soon' | 'expired'

/** 30 days in milliseconds */
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Determines the expiration status of a subscription period.
 *
 * @param periodEndUtcTs - Unix timestamp in SECONDS for the period end date, or null/undefined
 * @param nowMs - Current time in milliseconds (defaults to Date.now())
 * @returns 'expired' if past, 'renew_soon' if within 30 days, 'none' otherwise
 */
export function getExpirationStatus(
  periodEndUtcTs: number | null | undefined,
  nowMs?: number
): ExpirationStatus {
  if (periodEndUtcTs == null) return 'none'

  const now = nowMs ?? Date.now()
  const periodEndMs = periodEndUtcTs * 1000

  if (periodEndMs < now) return 'expired'
  if (periodEndMs - now <= THIRTY_DAYS_MS) return 'renew_soon'
  return 'none'
}

/**
 * Computes the whole number of calendar days remaining until the period end.
 * Returns null if periodEndUtcTs is null/undefined or already in the past.
 * Returns 0 if the period ends today.
 *
 * @param periodEndUtcTs - Unix timestamp in SECONDS for the period end date, or null/undefined
 * @param nowMs - Current time in milliseconds (defaults to Date.now())
 * @returns Whole number of days remaining (0–30+), or null if invalid/past
 */
export function getDaysRemaining(
  periodEndUtcTs: number | null | undefined,
  nowMs?: number
): number | null {
  if (periodEndUtcTs == null) return null

  const now = nowMs ?? Date.now()
  const periodEndMs = periodEndUtcTs * 1000

  if (periodEndMs < now) return null

  const diffMs = periodEndMs - now
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000))
  return days
}

/**
 * Formats the max executions value for display.
 *
 * @param value - The max executions number (0 means unlimited)
 * @returns "Unlimited" when value is 0, string representation otherwise
 */
export function formatMaxExecutions(value: number): string {
  if (value === 0) return 'Unlimited'
  return String(value)
}

/**
 * Capitalizes the first character of a status string.
 *
 * @param status - The status string to capitalize
 * @returns The status with first character uppercased and remainder unchanged
 */
export function capitalizeStatus(status: string): string {
  if (!status) return ''
  return status.charAt(0).toUpperCase() + status.slice(1)
}
